import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { logEvent, getAnalytics } from 'firebase/analytics';
import MultiStepActivationContainer from './Components/MultiStepActivationContainer';
import { useTranslation, Trans } from 'react-i18next';
import { themeColor } from '../Theming';
import images from '../Theming/images';
import { FirstA } from './Components/InstructionStep';
import { useNavigate } from 'react-router-dom';
import { Path } from '../Routing';
import { hasSliideOrigin } from '.';
import {
  MultiStepHeaderText,
  addSliideText,
} from './PartnerPluginActivateMultiStep';
import app from '../Firebase';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  PartnerNames,
  trackActivationActionEvent,
} from '../Common/Analytics/utils';
import { useQuery } from '../Common/Hooks';

const SubTitleTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SubTitleText = styled.div`
  color: ${themeColor('darkestGrey')};
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  width: 310px;
`;

const LinkText = styled.a`
  color: ${themeColor('blue')};
`;

export const HyperisePlaceholderContainer = styled.div`
  width: 100%;
  height: fit-content;
`;

const BodyContentContainer = styled.div`
  width: 320px;
  min-height: 106px;
  margin-top: 33px;
  margin-left: 35px;
`;

const ListContent = styled.li`
  color: ${themeColor('darkestGrey')};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  margin-left: 15px;
  margin-bottom: 20px;
`;

const BorderedItem = styled.strong`
  color: ${themeColor('blue')};
  border: 0.8px solid ${themeColor('blue')};
  padding: 3px;
  border-radius: 5px;
  margin-left: 3px;
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const StyledImage = styled.img`
  position: fixed;
  top: 8px;
  right: 10px;
  animation: ${bounce} 2s infinite;
`;

const PartnerPluginActivateMultiStepAlwaysAllow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sliide] = useState(hasSliideOrigin());
  const { additional_permissions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  const query = useQuery();
  const partner = (
    sliide ? 'sliide' : query.get('utm_campaign') ?? 'Kindred'
  ) as PartnerNames;

  useEffect(() => {
    const analytics = getAnalytics(app);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: any) => {
      if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            trackActivationActionEvent(
              query,
              partner,
              activation_permissions,
              additional_permissions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner,
            });

            if (message.data?.givenPermission) {
              navigate({
                pathname: Path.PluginActivationStartShopping,
                search: location.search,
              });
            } else {
              navigate({
                pathname: Path.PluginActivationAllowPermissions,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, [history]);

  return (
    <MultiStepActivationContainer
      headerTextWidth={240}
      headerTextColor="#323A68"
      sliideSubtitle="PartnerpluginsdkActivate.multiStep.theBrowserExtension"
      sliide={sliide}
      headerText={
        'PartnerpluginsdkActivate.multiStep.followTheseEasySteps' +
        addSliideText(sliide)
      }
    >
      <StyledImage
        src={
          sliide
            ? images.partnerpluginsdk_tap_here_sliide
            : images.partnerpluginsdk_tap_here
        }
      />
      <MultiStepHeaderText sliide={sliide}>
        {t('PartnerpluginsdkActivate.multiStep.alwaysAllow')}
      </MultiStepHeaderText>
      <SubTitleTextContainer>
        <SubTitleText>
          <Trans
            t={t}
            i18nKey={'PartnerpluginsdkActivate.multiStep.needToAlways'}
          >
            <LinkText>AbsoluteText</LinkText>
          </Trans>
        </SubTitleText>
      </SubTitleTextContainer>
      <BodyContentContainer>
        <ol>
          <ListContent>
            <Trans
              i18nKey="PartnerpluginsdkActivate.multiStep.alwaysAllowStep1"
              t={t}
            >
              <FirstA>A</FirstA>
            </Trans>
          </ListContent>
          <ListContent>
            <Trans
              i18nKey="PartnerpluginsdkActivate.multiStep.alwaysAllowStep2"
              t={t}
            >
              <BorderedItem>A</BorderedItem>
            </Trans>
          </ListContent>
          <ListContent>
            <Trans
              i18nKey="PartnerpluginsdkActivate.multiStep.alwaysAllowStep3"
              t={t}
            >
              <BorderedItem>A</BorderedItem>
            </Trans>
          </ListContent>
        </ol>
      </BodyContentContainer>
    </MultiStepActivationContainer>
  );
};

export default PartnerPluginActivateMultiStepAlwaysAllow;
